<template>
  <div class="sec">
    <div class="sec__inner">
      <h2 class="sec__title">{{ t("security.title") }}</h2>
      <p class="sec__lede">{{ t("security.lede") }}</p>

      <p v-if="loading" class="sec__note">{{ t("common.loading") }}…</p>
      <p v-else-if="methods === null" class="sec__note sec__note--bad" role="alert">{{ t("security.loadFailed") }}</p>

      <template v-else>
        <p v-if="message" class="sec__note" :class="{ 'sec__note--bad': failed }" role="status">{{ message }}</p>

        <!-- ── Password ──────────────────────────────────────────────────── -->
        <section class="sec__block">
          <div class="sec__row">
            <v-icon size="18" class="sec__icon">key</v-icon>
            <div class="sec__what">
              <span class="sec__name">{{ t("security.passwordTitle") }}</span>
              <span class="sec__detail">{{ methods.password ? t("security.passwordOn") : t("security.passwordOff") }}</span>
            </div>
            <!-- 🔴 Disabled from the SERVER's count, never from one this screen keeps.
                 The rule and the affordance cannot drift apart if only one of them counts. -->
            <v-btn
              v-if="methods.password"
              size="small"
              variant="text"
              :disabled="isLastMethod || busy"
              :title="isLastMethod ? t('security.lastMethod') : undefined"
              data-testid="remove-password"
              @click="remove('password')"
              >{{ t("security.remove") }}</v-btn
            >
          </div>
        </section>

        <!-- ── Passkeys ──────────────────────────────────────────────────── -->
        <section class="sec__block">
          <div class="sec__row">
            <v-icon size="18" class="sec__icon">fingerprint</v-icon>
            <div class="sec__what">
              <span class="sec__name">{{ t("security.passkeysTitle") }}</span>
              <span class="sec__detail">{{ passkeyDetail }}</span>
            </div>
          </div>
          <ul v-if="keys.length" class="sec__list" data-testid="passkey-list">
            <li v-for="key in keys" :key="key.id" class="sec__key">
              <span class="sec__key-name">{{ passkeyLabel(key, locale) ?? t("security.unnamed") }}</span>
              <!-- 🔴 Only when the person NAMED the key (PNK-149). Otherwise the label beside it is
                   already "<authenticator> · <date>", and this column repeated the date in a second,
                   differently-formatted copy: "Google Password Manager · 16 Sept 2026 … 9/16/2026". -->
              <span v-if="key.name && key.createdAt" class="sec__key-when">{{ added(key.createdAt) }}</span>
            </li>
          </ul>
          <v-btn
            v-if="passkeysUsable"
            class="sec__action"
            variant="tonal"
            color="primary"
            size="small"
            :loading="addingPasskey"
            data-testid="passkey-add"
            @click="addPasskey()"
          >
            <v-icon start size="16">add</v-icon>{{ t("security.add") }}
          </v-btn>
        </section>

        <!-- ── Google ────────────────────────────────────────────────────── -->
        <section v-if="methods.googleAvailable" class="sec__block">
          <div class="sec__row">
            <v-icon size="18" class="sec__icon">account_circle</v-icon>
            <div class="sec__what">
              <span class="sec__name">{{ t("security.googleTitle") }}</span>
              <span class="sec__detail">{{ googleAccounts.length === 0 ? t("security.googleNone") : t("security.googleConnected") }}</span>
            </div>
          </div>

          <!-- 🔴 ONE ROW PER ACCOUNT, NAMED. A single "Connected" line with one Remove was
               wrong the moment a second account existed: it said nothing about WHICH accounts
               were connected, and its Remove deleted every one of them. A person with a work
               and a personal Google account is the ordinary case. -->
          <ul v-if="googleAccounts.length" class="sec__list" data-testid="google-accounts">
            <li v-for="account in googleAccounts" :key="account.id" class="sec__key">
              <!-- referrerpolicy so Google is not told which page is showing the picture. -->
              <img
                v-if="account.picture && !brokenPictures.includes(account.id)"
                class="sec__face"
                :src="account.picture"
                alt=""
                referrerpolicy="no-referrer"
                loading="lazy"
                @error="pictureBroke(account.id)"
              />
              <!-- 🔴 Every row has a face. No picture, or one that would not load, gets the
                   monogram — a broken-image glyph is never the answer. -->
              <span v-else class="sec__face sec__face--mono" aria-hidden="true" :data-testid="`face-fallback-${account.id}`">
                <template v-if="account.email">{{ account.email.charAt(0).toUpperCase() }}</template>
                <v-icon v-else size="16">person</v-icon>
              </span>
              <span class="sec__key-name">{{ account.email ?? t("security.googleUnnamed") }}</span>
              <!-- 🔴 Only where the provider actually gave a picture. A button that cannot
                   work is worse than one that is not offered. -->
              <v-btn
                v-if="account.picture"
                size="small"
                variant="text"
                color="primary"
                :loading="usingPicture === account.id"
                :data-testid="`use-picture-${account.id}`"
                @click="usePicture(account.id)"
                >{{ t("security.usePicture") }}</v-btn
              >
              <v-btn
                size="small"
                variant="text"
                :disabled="isLastMethod || busy"
                :title="isLastMethod ? t('security.lastMethod') : undefined"
                :data-testid="`remove-google-${account.id}`"
                @click="remove(`social:${account.id}`)"
                >{{ t("security.remove") }}</v-btn
              >
            </li>
          </ul>
          <!-- 🔴 The sentence that answers "can I use my other address?" — yes, and this
               is where it is said, next to the button that does it. -->
          <p class="sec__hint">{{ t("security.googleHint") }}</p>
          <v-btn
            class="sec__action"
            variant="tonal"
            color="primary"
            size="small"
            :loading="connecting"
            data-testid="connect-google"
            @click="connectGoogle()"
          >
            <v-icon start size="16">link</v-icon>{{ t("security.connectGoogle") }}
          </v-btn>
        </section>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * 🔴 **HOW YOU SIGN IN** (D123, PNK-137, PNK-138) — `/security`.
 *
 * Devin: *"I also want an option for my users to change from a password to a SSO or a
 * passkey."* Changing means removing the old one, so this screen is as much about what
 * it REFUSES as what it offers.
 *
 * 🔴 **THE REMOVE BUTTONS ARE DISABLED FROM THE SERVER'S COUNT** (`methods.total`),
 * never from a count kept here. The server enforces "never your last way in" against the
 * database at the moment of the write — two tabs would otherwise each believe there were
 * two methods and remove a different one — and a screen that counted for itself would
 * eventually disagree with the rule it is meant to express.
 *
 * It lives in the shell rather than the profile module because the shell is the only
 * thing holding this tab's credential; see `auth/passkey.ts` for the same argument.
 */
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { listPasskeys, passkeysSupported, registerPasskey, type SavedPasskey } from "@/auth/passkey";
import { passkeyLabel } from "@/auth/authenticatorName";
import { fetchSignInMethods, photoFromAccount, removeSignInMethod, startGoogleLink, type SignInMethods } from "@/auth/signInMethods";

export default defineComponent({
  name: "Security",
  setup() {
    const { t, locale } = useI18n();
    const methods = ref<SignInMethods | null>(null);
    const keys = ref<SavedPasskey[]>([]);
    const loading = ref(true);
    const busy = ref(false);
    const addingPasskey = ref(false);
    const connecting = ref(false);
    /** Which account's picture is being copied, so only that button spins. */
    const usingPicture = ref<string | null>(null);
    const message = ref<string | null>(null);
    const failed = ref(false);
    /** Accounts whose picture would not load; they get the monogram instead. */
    const brokenPictures = ref<string[]>([]);
    function pictureBroke(accountId: string): void {
      brokenPictures.value = [...brokenPictures.value, accountId];
    }

    const passkeysUsable = passkeysSupported();
    const isLastMethod = computed(() => (methods.value?.total ?? 0) <= 1);
    const googleAccounts = computed(() => (methods.value?.social ?? []).filter((a) => a.provider === "google"));
    const passkeyDetail = computed(() => {
      if (!passkeysUsable) return t("security.unsupported");
      return keys.value.length === 0 ? t("security.empty") : `${keys.value.length}`;
    });

    async function refresh(): Promise<void> {
      methods.value = await fetchSignInMethods();
      keys.value = passkeysUsable ? await listPasskeys() : [];
    }

    onMounted(async () => {
      try {
        await refresh();
      } finally {
        loading.value = false;
      }
    });

    async function remove(method: string): Promise<void> {
      if (busy.value) return;
      message.value = null;
      failed.value = false;
      busy.value = true;
      try {
        const outcome = await removeSignInMethod(method);
        if (outcome.ok) {
          methods.value = outcome.remaining;
          message.value = t("security.removed");
          return;
        }
        // 🔴 The server's own sentence — including "this is the only way you can sign in",
        // which must not be softened into something a person could read past.
        failed.value = true;
        message.value = outcome.message;
      } finally {
        busy.value = false;
      }
    }

    async function addPasskey(): Promise<void> {
      if (addingPasskey.value) return;
      message.value = null;
      failed.value = false;
      addingPasskey.value = true;
      try {
        // 🔴 `null`, deliberately (PNK-149): this value becomes WebAuthn's `user.name`, the
        // identifier the OS shows in its own passkey list. It used to be today's date.
        const outcome = await registerPasskey(null);
        if (outcome.ok) {
          message.value = t("security.added");
          await refresh();
          return;
        }
        if (outcome.cancelled) return; // a dismissed prompt says nothing
        failed.value = true;
        message.value = outcome.message;
      } finally {
        addingPasskey.value = false;
      }
    }

    /**
     * Copy a connected account's picture into the person's own gallery.
     *
     * 🔴 It does NOT make it the avatar. Choosing which photograph is the face already has a
     * home on the profile screen (D70/D72), and a second place to choose would be a second
     * answer to the same question.
     */
    async function usePicture(accountId: string): Promise<void> {
      if (usingPicture.value !== null) return;
      message.value = null;
      failed.value = false;
      usingPicture.value = accountId;
      try {
        const outcome = await photoFromAccount(accountId);
        if (outcome.ok) {
          message.value = t("security.pictureAdded");
          return;
        }
        failed.value = true;
        message.value = outcome.message;
      } finally {
        usingPicture.value = null;
      }
    }

    async function connectGoogle(): Promise<void> {
      if (connecting.value) return;
      message.value = null;
      failed.value = false;
      connecting.value = true;
      // On success the browser leaves for Google, so nothing after this runs.
      const went = await startGoogleLink();
      if (!went) {
        failed.value = true;
        message.value = t("security.loadFailed");
        connecting.value = false;
      }
    }

    // 🔴 `locale.value`, never bare `toLocaleDateString()` — the no-argument form is the BROWSER's
    // locale, which is how an en-ZA app rendered "9/16/2026" (PNK-149). This was the third copy of
    // that mistake in one feature.
    const added = (iso: string): string => {
      const when = new Date(iso);
      return Number.isNaN(when.getTime())
        ? ""
        : when.toLocaleDateString(locale.value, { day: "numeric", month: "short", year: "numeric" });
    };

    return {
      t, locale, passkeyLabel, methods, keys, loading, busy, addingPasskey, connecting, message, failed,
      passkeysUsable, isLastMethod, googleAccounts, passkeyDetail, remove, addPasskey, connectGoogle, added,
      usingPicture, usePicture, brokenPictures, pictureBroke,
    };
  },
});
</script>

<style scoped>
/* App.vue wraps this in <v-main>, which accounts for the app bar — the same
   arrangement AppHome has. A second allowance would push the page down twice. */
.sec__inner { max-width: 560px; margin: 0 auto; padding: 24px 16px 48px; }
.sec__title { margin: 0 0 6px; font-size: 20px; font-weight: 700; }
.sec__lede { margin: 0 0 20px; font-size: 13.5px; line-height: 1.55; color: var(--sh-text-60); }
.sec__note { margin: 0 0 16px; font-size: 13px; color: var(--sh-text-60); }
.sec__note--bad { color: rgb(var(--v-theme-error)); }
.sec__block {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: var(--sh-radius);
  background: rgb(var(--v-theme-surface));
}
.sec__row { display: flex; align-items: center; gap: 10px; }
.sec__icon { color: rgb(var(--v-theme-primary)); }
.sec__what { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sec__name { font-size: 13.5px; font-weight: 600; }
.sec__detail { font-size: 12px; color: var(--sh-text-60); overflow-wrap: anywhere; }
.sec__hint { margin: 10px 0 0; font-size: 12px; line-height: 1.5; color: var(--sh-text-60); }
.sec__action { margin-top: 10px; font-weight: 600; }
.sec__list { list-style: none; margin: 10px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.sec__key {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: calc(var(--sh-radius) - 2px);
}
.sec__key-name { flex: 1; font-size: 13px; overflow-wrap: anywhere; }
.sec__face { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex: none; }
/* The same monogram the sidebar draws for somebody without a photograph. */
.sec__face--mono {
  display: grid;
  place-items: center;
  background: rgb(var(--v-theme-secondary));
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
}
.sec__key-when { font-size: 11.5px; color: var(--sh-text-60); white-space: nowrap; }
</style>
